import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiError } from '../../../abstract/api-error';
import { OrganizationSeasonService } from '../../../core/data-services/organization-season.service';
import { OrganizationSeason } from '../../../core/data-models/organization-season.model';
import { OrganizationSeasonCreateRequest } from '../../../core/request-models/organization-season-create-request.model';

/**
 * Organization Seasons Tab Component
 *
 * Displays and manages agricultural spraying seasons for an organization.
 * Used within the admin organization detail page.
 * Includes client-side overlap detection with soft warnings.
 *
 * Reference: /agent-os/specs/2026-02-11-acres-advantage/spec.md
 */
@Component({
  selector: 'app-organization-seasons-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './organization-seasons-tab.component.html',
  styleUrl: './organization-seasons-tab.component.scss'
})
export class OrganizationSeasonsTabComponent implements OnInit {
  @Input() organizationId!: string;

  seasons: OrganizationSeason[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';

  // Add season form
  showAddForm = false;
  newSeason: OrganizationSeasonCreateRequest = {
    label: '',
    startDate: '',
    endDate: ''
  };
  addingSeasonError = '';
  addingSeason = false;

  // Overlap warning (soft warning, does not block saving)
  overlapWarning = '';

  constructor(private seasonsService: OrganizationSeasonService) {}

  ngOnInit(): void {
    this.loadSeasons();
  }

  loadSeasons(): void {
    this.loading = true;
    this.errorMessage = '';

    this.seasonsService.listSeasons(this.organizationId).subscribe({
      next: (seasons) => {
        this.seasons = seasons;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load seasons:', err);
        this.errorMessage = 'Failed to load seasons';
        this.loading = false;
      }
    });
  }

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    this.addingSeasonError = '';
    this.overlapWarning = '';
    if (this.showAddForm) {
      this.newSeason = { label: '', startDate: '', endDate: '' };
    }
  }

  /**
   * Check if the new season dates overlap with any existing season.
   * Called when start or end date changes in the form.
   * Two date ranges overlap if newStart <= existingEnd AND newEnd >= existingStart.
   */
  checkSeasonOverlap(): void {
    this.overlapWarning = '';

    if (!this.newSeason.startDate || !this.newSeason.endDate) return;

    const newStart = this.newSeason.startDate;
    const newEnd = this.newSeason.endDate;

    for (const season of this.seasons) {
      const existingStart = season.startDate;
      const existingEnd = season.endDate;
      if (!existingStart || !existingEnd) continue;

      if (newStart <= existingEnd && newEnd >= existingStart) {
        this.overlapWarning =
          `Warning: This season overlaps with "${season.label}" (${this.formatDate(existingStart)} - ${this.formatDate(existingEnd)}). Overlapping seasons may cause unexpected behavior in points calculations.`;
        return;
      }
    }
  }

  submitSeason(): void {
    this.addingSeasonError = '';

    if (!this.newSeason.label.trim()) {
      this.addingSeasonError = 'Season label is required';
      return;
    }
    if (!this.newSeason.startDate) {
      this.addingSeasonError = 'Start date is required';
      return;
    }
    if (!this.newSeason.endDate) {
      this.addingSeasonError = 'End date is required';
      return;
    }
    if (this.newSeason.endDate <= this.newSeason.startDate) {
      this.addingSeasonError = 'End date must be after start date';
      return;
    }

    this.addingSeason = true;

    this.seasonsService.createSeason(this.organizationId, this.newSeason).subscribe({
      next: () => {
        this.showAddForm = false;
        this.addingSeason = false;
        this.overlapWarning = '';
        this.showSuccess('Season created successfully');
        this.loadSeasons();
      },
      error: (err) => {
        console.error('Failed to create season:', err);
        this.addingSeasonError = ApiError.fromHttpError(err).message;
        this.addingSeason = false;
      }
    });
  }

  getSeasonStatus(season: OrganizationSeason): 'Active' | 'Past' | 'Future' {
    if (!season.startDate || !season.endDate) return 'Active';
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(season.startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(season.endDate);
    end.setHours(0, 0, 0, 0);

    if (start > today) return 'Future';
    if (end < today) return 'Past';
    return 'Active';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Active': return 'bg-success';
      case 'Past': return 'bg-secondary';
      case 'Future': return 'bg-info';
      default: return 'bg-secondary';
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 4000);
  }
}
